import { Component, effect, inject, ViewChild } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DocSubmissionService } from './doc-submission.service';
import { DocSubmissionRepository } from '@/store/doc-submission-state/doc-submission-state.repository';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { DialogObject } from '@/components/dialog/dialog.types';
import { getDocSubmissionFormObject } from './utils/doc-submission-form-object';
import { BehaviorSubject } from 'rxjs';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { getDocSubmissionUpdateDrawerObject } from './utils/doc-submission-update-drawer-object';
import { getDocSubmissionDeleteObject } from './utils/doc-submission-delete-dialog-object';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { getDocSubmissionDataTableStructure } from './utils/doc-submission-data-table-object';
import { getDocSubmissionAddDrawerObject } from './utils/doc-submission-add-drawer-object';
import { DocSubmission, DocSubmissionDto } from '@/types';
import { docSubmissionSchema } from '@/validations/doc-submission.validation';
import { SelectOption } from '@/components/form-builder/form-builder.types';

@Component({
  selector: 'sm-doc-submission',
  imports: [PageHeaderComponent, DataTableBuilderComponent, DrawerComponent, DialogComponent],
  templateUrl: './doc-submission.component.html',
  styleUrl: './doc-submission.component.scss',
})
export class DocSubmissionComponent {
  translate = inject(TranslateService);
  messageService = inject(MessageService);
  docSubmissionService = inject(DocSubmissionService);
  docSubmissionRepository = inject(DocSubmissionRepository);

  breadcrumb = [
    {
      label: 'settings.docSubmission.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.docSubmission.breadcrumb.docSubmission',
      routerLink: ['/settings', 'docSubmission'],
    },
  ];

  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  docSubmissionDrawer?: DrawerObject;

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  docSubmissionDialog?: DialogObject;

  docSubmissionCategoriesOptions = new BehaviorSubject<SelectOption[]>([]);

  formObject = getDocSubmissionFormObject({
    store: this.docSubmissionRepository,
    docSubmissionTypesOptions: this.docSubmissionCategoriesOptions,
  });

  isDisabled = new BehaviorSubject<boolean>(true);

  rowActions: DataTableRowActions = {
    editAction: (row: DocSubmission) => {
      this.docSubmissionDrawer = getDocSubmissionUpdateDrawerObject({
        store: this.docSubmissionRepository,
        formObject: this.formObject,
        action: () => this.updateDocSubmission(row.id, this.docSubmissionRepository.get('dto')),
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
    deleteAction: (row: DocSubmission) => {
      this.docSubmissionDialog = getDocSubmissionDeleteObject({
        store: this.docSubmissionRepository,
        action: () => {
          this.deleteDocSubmission(row.id);
          this.dialogComponent.toggleVisible();
        },
        toggleVisibility: () => this.dialogComponent.toggleVisible(),
      });
      this.dialogComponent.toggleVisible();
    },
  };

  loading = true;
  serverQuery = createServerQuery({});
  data: DocSubmission[] = [];
  totalRecords = 0;

  dataTableObject: DynamicDataTable = getDocSubmissionDataTableStructure({
    store: this.docSubmissionRepository,
    rowActions: this.rowActions,
    createAction: () => {
      this.docSubmissionDrawer = getDocSubmissionAddDrawerObject({
        store: this.docSubmissionRepository,
        formObject: this.formObject,
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
        action: () => this.addDocSubmission(),
      });
      this.drawerComponent.toggleVisible();
    },
    enableServerActions: true,
    serverQuery: this.serverQuery,
  });

  constructor() {
    effect(() => {
      const page = this.serverQuery.page();
      const size = this.serverQuery.pageSize();
      const search = this.serverQuery.search();
      const sortBy = this.serverQuery.sortBy();
      const sortOrder = this.serverQuery.sortOrder();

      this.loadData(page, size, search, sortBy, sortOrder);
    });
  }

  loadData(page = 0, size = 10, search = '', sortBy = '', sortOrder = 'asc') {
    const sort = sortBy ? `${sortBy},${sortOrder}` : '';
    this.docSubmissionService.getAll({ page, size, search, sort }).subscribe({
      next: data => {
        this.totalRecords = (data as any).totalElements ?? data.content.length;
        this.data = (data as any).content ?? [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.docSubmission.message.error.loading'),
        });
      },
    });
    this.docSubmissionService.getAllFolderTypes().subscribe({
      next: data => {
        this.docSubmissionCategoriesOptions.next(
          data.map(typeDossier => ({
            name: typeDossier.libelle,
            code: typeDossier.id,
          }))
        );
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message,
        });
      },
    });
  }

  addDocSubmission() {
    const result = docSubmissionSchema.safeParse(this.docSubmissionRepository.get('dto'));
    if (result.success) {
      this.docSubmissionService.create(this.docSubmissionRepository.get('dto')).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData(
            this.serverQuery.page(),
            this.serverQuery.pageSize(),
            this.serverQuery.search(),
            this.serverQuery.sortBy(),
            this.serverQuery.sortOrder()
          );
          this.docSubmissionRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.docSubmission.message.success.added'),
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.docSubmission.message.error.added'),
          });
        },
      });
    } else {
      this.docSubmissionRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  updateDocSubmission(id: number, docSubmission: DocSubmissionDto) {
    const result = docSubmissionSchema.safeParse(docSubmission);
    if (result.success) {
      this.docSubmissionService.update(id, docSubmission).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData(
            this.serverQuery.page(),
            this.serverQuery.pageSize(),
            this.serverQuery.search(),
            this.serverQuery.sortBy(),
            this.serverQuery.sortOrder()
          );
          this.docSubmissionRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.docSubmission.message.success.edited'),
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.docSubmission.message.error.edited'),
          });
        },
      });
    } else {
      this.docSubmissionRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  deleteDocSubmission(id: number) {
    this.docSubmissionService.delete(id).subscribe({
      next: () => {
        this.loadData(
          this.serverQuery.page(),
          this.serverQuery.pageSize(),
          this.serverQuery.search(),
          this.serverQuery.sortBy(),
          this.serverQuery.sortOrder()
        );
        this.docSubmissionRepository.reset();
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.docSubmission.message.success.deleted'),
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.docSubmission.message.error.deleted'),
        });
      },
    });
  }
}
